import React, { useState, useEffect, useCallback } from 'react';
import { Globe, Eye, EyeOff, Save, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { supabase } from '../../lib/supabaseClient';

const ROLE_TYPES = [
  'Software Engineering',
  'Data & AI',
  'Cloud & DevOps',
  'Cybersecurity',
  'Project Delivery',
  'Architecture',
  'Business Analysis & UX',
  'QA & Testing',
  'Infrastructure',
];

const SENIORITY_LEVELS = ['Junior', 'Mid career', 'Senior', 'Lead', 'Management', 'Executive'];
const CLEARANCE_LEVELS = ['None', 'Baseline', 'NV1', 'NV2', 'TSPV'];

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-muted-foreground mb-1">
      {label}
      {hint && <span className="ml-1.5 text-xs font-normal text-muted-foreground/60">{hint}</span>}
    </label>
    {children}
  </div>
);

const inputCls =
  'w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-colors';

const CandidatePublicDisplay = ({ candidate, skills }) => {
  const defaultLocation =
    candidate.location_city && candidate.location_state
      ? `${candidate.location_city}, ${candidate.location_state}`
      : candidate.location_city || candidate.location_state || '';

  const defaultSkillsStr = skills
    .slice(0, 8)
    .map((cs) => cs.skill?.name)
    .filter(Boolean)
    .join(', ');

  const buildDefaults = useCallback(
    () => ({
      is_public: false,
      display_role: candidate.position?.name || candidate.position_title || '',
      role_type: '',
      seniority: '',
      pitch: '',
      display_skills: defaultSkillsStr,
      display_location: defaultLocation,
      display_notice: candidate.notice_period || '',
      display_clearance: candidate.clearance_level || 'None',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidate.id]
  );

  const [profile, setProfile] = useState(buildDefaults);
  const [loadState, setLoadState] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      setLoadState('loading');
      try {
        const { data, error } = await supabase
          .from('candidate_public_profiles')
          .select('*')
          .eq('candidate_id', candidate.id)
          .maybeSingle();

        if (error) throw error;
        if (!cancelled) {
          if (data) {
            setProfile({
              is_public: data.is_public,
              display_role: data.display_role || '',
              role_type: data.role_type || '',
              seniority: data.seniority || '',
              pitch: data.pitch || '',
              display_skills: (data.display_skills || []).join(', '),
              display_location: data.display_location || defaultLocation,
              display_notice: data.display_notice || candidate.notice_period || '',
              display_clearance: data.display_clearance || candidate.clearance_level || 'None',
            });
          } else {
            setProfile(buildDefaults());
          }
          setLoadState('ready');
        }
      } catch {
        if (!cancelled) setLoadState('error');
      }
    };

    fetch();
    return () => { cancelled = true; };
  }, [candidate.id, buildDefaults, defaultLocation, candidate.notice_period, candidate.clearance_level]);

  const update = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
    setSaveState('idle');
  };

  const handleSave = async () => {
    setSaveState('saving');
    setSaveError(null);

    const skillsArray = profile.display_skills
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const { error } = await supabase
      .from('candidate_public_profiles')
      .upsert(
        {
          candidate_id: candidate.id,
          is_public: profile.is_public,
          display_role: profile.display_role,
          role_type: profile.role_type,
          seniority: profile.seniority,
          pitch: profile.pitch,
          display_skills: skillsArray,
          display_location: profile.display_location,
          display_notice: profile.display_notice,
          display_clearance: profile.display_clearance,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'candidate_id' }
      );

    if (error) {
      setSaveState('error');
      setSaveError(error.message);
    } else {
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 4000);
    }
  };

  const skillTags = profile.display_skills
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  if (loadState === 'loading') {
    return (
      <Card className="w-full max-w-full overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Globe className="h-5 w-5" /> Public Display
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loadState === 'error') {
    return (
      <Card className="w-full max-w-full overflow-hidden border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Globe className="h-5 w-5" /> Public Display
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">
            Could not load public profile. Make sure the <code>candidate_public_profiles</code> table
            migration has been applied in Supabase.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`w-full max-w-full overflow-hidden transition-colors ${
        profile.is_public ? 'border-green-300 ring-1 ring-green-200' : ''
      }`}
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Globe className="h-5 w-5 shrink-0" />
              Public Display
            </CardTitle>
            <CardDescription className="mt-1">
              Control this candidate's listing on the public <em>Available Candidates</em> page.
              No real names or contact details will be shown.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 shrink-0 pt-0.5">
            {profile.is_public ? (
              <Badge className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1.5">
                <Eye className="h-3 w-3" /> Listed
              </Badge>
            ) : (
              <Badge variant="secondary" className="flex items-center gap-1.5">
                <EyeOff className="h-3 w-3" /> Hidden
              </Badge>
            )}
            <Switch
              checked={profile.is_public}
              onCheckedChange={(v) => update('is_public', v)}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Role title — full width */}
          <div className="md:col-span-2">
            <Field label="Display Role Title" hint="shown as the card heading">
              <input
                type="text"
                value={profile.display_role}
                onChange={(e) => update('display_role', e.target.value)}
                placeholder="e.g. Senior Data Engineer"
                className={inputCls}
              />
            </Field>
          </div>

          {/* Role type */}
          <Field label="Role Type" hint="used for the filter dropdown">
            <select
              value={profile.role_type}
              onChange={(e) => update('role_type', e.target.value)}
              className={inputCls}
            >
              <option value="">Select role type…</option>
              {ROLE_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>

          {/* Seniority */}
          <Field label="Seniority Level">
            <select
              value={profile.seniority}
              onChange={(e) => update('seniority', e.target.value)}
              className={inputCls}
            >
              <option value="">Select seniority…</option>
              {SENIORITY_LEVELS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>

          {/* Clearance */}
          <Field label="Clearance (displayed)">
            <select
              value={profile.display_clearance}
              onChange={(e) => update('display_clearance', e.target.value)}
              className={inputCls}
            >
              {CLEARANCE_LEVELS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>

          {/* Location */}
          <Field label="Location (displayed)">
            <input
              type="text"
              value={profile.display_location}
              onChange={(e) => update('display_location', e.target.value)}
              placeholder="e.g. Canberra, ACT"
              className={inputCls}
            />
          </Field>

          {/* Notice / availability */}
          <Field label="Availability (displayed)" hint="e.g. Available now · 2 weeks notice">
            <input
              type="text"
              value={profile.display_notice}
              onChange={(e) => update('display_notice', e.target.value)}
              placeholder="Available now"
              className={inputCls}
            />
          </Field>

          {/* Skills — full width */}
          <div className="md:col-span-2 space-y-2">
            <Field label="Key Skills" hint="comma-separated, shown as tags on the card">
              <input
                type="text"
                value={profile.display_skills}
                onChange={(e) => update('display_skills', e.target.value)}
                placeholder="e.g. Python, Databricks, Azure, ETL"
                className={inputCls}
              />
            </Field>
            {skillTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {skillTags.map((skill) => (
                  <span
                    key={skill}
                    className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Pitch — full width */}
          <div className="md:col-span-2">
            <Field
              label="Public Pitch"
              hint="— 2–3 sentences shown on the card. No names or contact details."
            >
              <textarea
                rows={5}
                value={profile.pitch}
                onChange={(e) => update('pitch', e.target.value)}
                placeholder="Describe this candidate's background, experience and clearance in 2–3 sentences. Keep it anonymous — no real names or identifying information."
                className={`${inputCls} resize-none`}
              />
            </Field>
            <p className="mt-1 text-xs text-muted-foreground">{profile.pitch.length} characters</p>
          </div>
        </div>

        {/* Error message */}
        {saveState === 'error' && saveError && (
          <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">
            {saveError}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-4 pt-3 border-t">
          <p className="text-xs text-muted-foreground">
            {profile.is_public
              ? 'This candidate is listed on the public page.'
              : 'Toggle the switch above to list this candidate publicly.'}
            {' '}Changes are not live until saved.
          </p>
          <Button onClick={handleSave} disabled={saveState === 'saving'} size="sm" className="shrink-0">
            {saveState === 'saving' ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
            ) : saveState === 'saved' ? (
              <span className="text-green-600 font-semibold">✓ Saved</span>
            ) : (
              <><Save className="h-4 w-4 mr-2" />Save changes</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CandidatePublicDisplay;
